import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { isWeb, isReactNative } from '../utils/platform';
import { signOut as platformSignOut } from '../services/authService';

// Platform-agnostic storage
const getStorage = () => {
  if (isWeb()) {
    return {
      getItem: (key) => Promise.resolve(localStorage.getItem(key)),
      setItem: async (key, value) => localStorage.setItem(key, value),
      removeItem: (key) => localStorage.removeItem(key),
    };
  } else if (isReactNative()) {
    // Dynamically import AsyncStorage for React Native
    // Use Function constructor to prevent webpack from analyzing the require
    // This ensures React Native packages are never bundled in web builds
    try {
      // eslint-disable-next-line no-new-func
      const requireModule = new Function('moduleName', 'return require(moduleName)');
      const AsyncStorage = requireModule('@react-native-async-storage/async-storage').default;
      return AsyncStorage;
    } catch (e) {
      // Fallback if AsyncStorage not available (web environment during build)
      return {
        getItem: (key) => Promise.resolve(localStorage?.getItem(key) || null),
        setItem: async (key, value) => localStorage?.setItem(key, value),
        removeItem: (key) => localStorage?.removeItem(key),
      };
    }
  }
  // Fallback for web
  return {
    getItem: (key) => Promise.resolve(localStorage?.getItem(key) || null),
    setItem: async (key, value) => localStorage?.setItem(key, value),
    removeItem: (key) => localStorage?.removeItem(key),
  };
};

const storage = getStorage();

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    // Get user profile from backend when Firebase user changes
    const fetchUserProfile = async (firebaseUser) => {
        try {
            const idToken = await firebaseUser.getIdToken();
            
            // Get API base URL based on platform
            const apiBaseUrl = isWeb() 
                ? 'http://localhost:5001/api' 
                : (process.env.REACT_NATIVE_API_BASE_URL || 
                   process.env.REACT_APP_API_BASE_URL || 
                   'http://localhost:5001/api');
            
            const response = await fetch(`${apiBaseUrl}/firebase-auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ idToken }),
            });
            
            const data = await response.json();
            if (data.success) {
                setUserProfile(data.data.user);
                // Store the ID token for future API calls (platform-agnostic)
                await storage.setItem('firebaseIdToken', idToken);
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                setUser(firebaseUser);
                await fetchUserProfile(firebaseUser);
            } else {
                setUser(null);
                setUserProfile(null);
                // Clear storage (platform-agnostic)
                storage.removeItem('firebaseIdToken');
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const refreshUserProfile = async () => {
        if (user) {
            await fetchUserProfile(user);
        }
    };

    const logout = async () => {
        try {
            // Use platform-agnostic sign out
            await platformSignOut(auth, storage);
            setUser(null);
            setUserProfile(null);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const value = {
        user,
        userProfile,
        loading,
        refreshUserProfile,
        logout,
        isAuthenticated: !!user && !!userProfile
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
